import classNames from 'classnames'
import * as React from 'react'

import { Analytics } from '../../../Global/Analytics'
import { AnalyticsType, Category, Events, Placement, Result } from '../../../Global/AnalyticsEvents'
import { GlobalEvents } from '../../../Other/GlobalEvents'
import { Spinner, SpinnerType } from '../../spinner/Spinner'
import { TestableInterface } from '../interfaces/TestableInterface'
import { ValidatableInterface } from '../interfaces/ValidatableInterface'
import { formatRequiredIndicator } from '../Required/Required'
import { RequiredInterface } from '../Required/RequiredInterface'
import './SearchSelect.scss'

const SEARCH_DELAY = 500
const SEARCH_MIN_LENGTH = 3

export interface SelectSearchDataSourceInterface {
  title: string
  name: string
  linkUrl: string
  logoFile?: string
}

export interface SearchSelectCachePropInterface {
  // max number of results that the backend returns for one call
  maxResults: number
  // name of the field used for filtering, e.g. 'name' for company
  searchableField: string
}

export interface ExternalPropsInterface extends TestableInterface, RequiredInterface, ValidatableInterface {
  value: string
  label: string
  doSearch: (searchTerm) => any
  resultRowRenderer: (result, searchTerm) => any
  dataSource?: SelectSearchDataSourceInterface
  noResultsMessage?: string
  searchingMessage?: string
  onSelect: (value) => void
  onBlur: (value) => void
  /**
   * Optional cache: If set and backend has returned less results than [maxResults] and search term still contains
   * the search term saved in the cache, return results from cache filtered by value in searchableField.
   * If the search term changes, we get new results from the backend.
   */
  useCache?: SearchSelectCachePropInterface
  disabled?: boolean
  placeHolder?: string
  className?: string[]
  placement?: string // for analytics
  onChange?: (value: string) => any
  autoFocus?: boolean
}

export interface OwnStateInterface {
  searching: boolean
  searchTerm: string
  results?: any
  open: boolean
  index?: number
}

export interface SearchSelectCacheInterface {
  searchTerm: string
  results: any
}

const SearchingMessage = (props) => {
  return (
    <div className="search-select__results__searching">
      <span>{props.message}</span>
    </div>
  )
}

const NoResultsMessage = (props) => {
  return (
    <div className="search-select__results__no-results">
      <span>{props.message}</span>
    </div>
  )
}

const SearchDataSource = (props) => {
  if (!props.dataSource) {
    return null
  }
  return (
    <div
      className="search-select__datasource"
      style={props.dataSource.linkUrl ? { ...{ cursor: 'pointer' } } : {}}
      onClick={() => {
        GlobalEvents.prevent(props.e)
        if (props.dataSource.linkUrl) {
          window.open(props.dataSource.linkUrl, '_blank')
        }
      }}
    >
      {props.dataSource.logoFile && <img src={props.dataSource.logoFile} />}
      <span>
        {props.dataSource.title}
        <br />
        {props.dataSource.name}
      </span>
    </div>
  )
}

const SearchSpinner = () => {
  return (
    <div className="search-select__searching">
      <Spinner type={SpinnerType.INLINE} />
    </div>
  )
}

export class SearchSelect extends React.Component<ExternalPropsInterface, OwnStateInterface> {
  constructor(props: ExternalPropsInterface) {
    super(props)
    this.state = {
      searching: false,
      searchTerm: props.value,
      open: false,
      results: [],
    }
  }

  private timer = null
  private e
  // Used to narrow results in the ui as the user types, only if we know that there are no more hits in the backend.
  // Using this requires parameters maxResults and searchableField.
  private cache: SearchSelectCacheInterface = null
  // Used to store results from backend so that we don't need to do the same requests again.
  private cachedRequests: SearchSelectCacheInterface[] = []
  private input: HTMLInputElement
  private onMouseEnterEnabled: boolean = true

  public componentDidMount() {
    this.e = GlobalEvents.Click((e) => {
      this.onBlur(this.state.searchTerm)
    })
    if (this.props.autoFocus && this.input) {
      this.input.focus()
    }
  }

  public componentWillUnmount() {
    GlobalEvents.unBind(this.e)
    clearTimeout(this.timer)
  }

  public UNSAFE_componentWillReceiveProps(nextProps: Readonly<ExternalPropsInterface>, nextContext: any): void {
    if (nextProps.value) {
      this.setState({ searchTerm: nextProps.value })
    }
  }

  private doSearch = async () => {
    this.setState({ searching: true, results: [] })
    const searchTerm = this.state.searchTerm.toLowerCase().trim()
    const results = await this.props.doSearch(searchTerm)
    if (!this.state.searching) {
      // search was cancelled, for example tabbing to next field
      return
    }
    if (results) {
      if (this.props.useCache) {
        this.cache = {
          results,
          searchTerm,
        }
      }
      this.cachedRequests.push({ searchTerm, results })
    }
    this.setState({ results, searching: searchTerm !== this.state.searchTerm.toLowerCase().trim(), open: true })
  }

  private handleOnChange = (e: Event) => {
    const value: string = (e.target as HTMLInputElement).value
    const openResultsAlready = Boolean(this.props.searchingMessage)
    this.setState({
      searchTerm: value,
      open: this.state.open || openResultsAlready,
      index: -1,
    })
    const valueLc = value.toLowerCase().trim()
    clearTimeout(this.timer)
    if (valueLc.length >= SEARCH_MIN_LENGTH) {
      // can be found in local cache, filter in the ui based on the search term
      if (
        this.props.useCache &&
        this.cache &&
        this.state.results.length > 0 &&
        this.state.results.length < this.props.useCache.maxResults &&
        valueLc.indexOf(this.cache.searchTerm.trim()) >= 0
      ) {
        this.setState({
          open: true,
          results: this.cache.results.filter(
            (r) => r[this.props.useCache.searchableField].toLowerCase().indexOf(valueLc) >= 0,
          ),
          searching: false,
        })
      } else if (this.cachedRequests.find((c) => c.searchTerm === valueLc)) {
        // already in cached results, return from there
        const results = this.cachedRequests.filter((c: SearchSelectCacheInterface) => c.searchTerm === valueLc)[0]
        this.setState({
          results: results.results,
          open: true,
          searching: false,
        })
      } else {
        // fetch stuff from backend
        this.timer = setTimeout(this.doSearch, SEARCH_DELAY)
        this.setState({ searching: true })
      }
    } else if (value === '') {
      this.setState({ results: [], open: false, searching: false })
      if (this.cache) {
        this.cache = null
      }
    }
    if (this.props.onChange) {
      this.props.onChange(value)
    }
  }

  private onBlur(value) {
    clearTimeout(this.timer)
    this.setState({ open: false, searching: false, results: [] })
    this.props.onBlur(value || this.state.searchTerm)
  }

  private handleOnBlur(e: Event) {
    clearTimeout(this.timer)
    this.setState({ searching: false })
    if (this.props.onBlur) {
      ;(e.target as HTMLInputElement).value = this.state.searchTerm
      this.props.onBlur(this.state.searchTerm)
    }
  }

  private static scrollSelectedToView() {
    const selected = document.getElementsByClassName('selected')
    if (selected && selected[0]) {
      selected[0].scrollIntoView({ block: 'center', behavior: 'auto' })
    }
  }

  // handle closing the list when user presses enter, tab and esc
  // and changing selection with arrows and selecting with enter
  private onKeyDown(e) {
    const numberOfResults = this.state.results ? this.state.results.length : 0
    switch (e.keyCode) {
      case 9: // tab
      case 27: // esc
        this.onBlur(this.state.searchTerm)
        break
      case 13: // enter
        if (this.state.index >= 0) {
          this.setState({ open: false, searching: false })
          this.props.onSelect(this.state.results[this.state.index])
        } else {
          this.onBlur(this.state.searchTerm)
        }
        break
      case 38: // up
        if (this.state.index >= 0) {
          this.setState({ index: this.state.index - 1 })
        } else {
          this.setState({ index: numberOfResults - 1 })
        }
        this.onMouseEnterEnabled = false
        setTimeout(SearchSelect.scrollSelectedToView, 100)
        break
      case 40: // down
        if (this.state.index >= 0 && this.state.index < numberOfResults - 1) {
          this.setState({ index: this.state.index + 1 })
        } else if (this.state.index === numberOfResults - 1) {
          this.setState({ index: -1 })
        } else {
          this.setState({ index: 0 })
        }
        this.onMouseEnterEnabled = false
        setTimeout(SearchSelect.scrollSelectedToView, 100)
        break
    }
  }

  private analytics(event: string, payload: any) {
    Analytics.trackEvent(`${event}`, {
      type: AnalyticsType.action,
      category: Category.setting,
      result: Result.success,
      placement: this.props.placement || '',
      payload,
    })
  }

  private renderResult(result, index): JSX.Element {
    return (
      <div
        className={classNames('search-select__results__result', { selected: index === this.state.index })}
        key={index}
        onClick={() => {
          GlobalEvents.prevent(this.e)
          this.setState({ open: false })
          this.props.onSelect(result)
          this.analytics(Events.companySearchSelect, {
            companyCode: result.code,
            companyName: result.name,
          })
        }}
        onMouseEnter={() => {
          if (this.onMouseEnterEnabled) {
            this.setState({ index })
          } else {
            this.onMouseEnterEnabled = true
          }
        }}
      >
        {this.props.resultRowRenderer(result, this.state.searchTerm)}
      </div>
    )
  }

  private renderResults(): JSX.Element {
    if (
      this.state.results.length === 0 &&
      !this.props.noResultsMessage &&
      (!this.state.searching || !this.props.searchingMessage)
    ) {
      return null
    }
    const results = this.state.results.map((result, index) => this.renderResult(result, index))
    return (
      <div className="search-select__results" data-automation={`${this.props.automation}-results`}>
        {this.state.searching && this.props.searchingMessage && (
          <SearchingMessage message={this.props.searchingMessage} />
        )}
        {results.length > 0 && <div className="search-select__results__list">{results}</div>}
        {results.length === 0 && this.props.noResultsMessage && (
          <NoResultsMessage message={this.props.noResultsMessage} />
        )}
        {(this.state.searching || results.length > 0) && (
          <SearchDataSource dataSource={this.props.dataSource} automation={this.props.automation} e={this.e} />
        )}
      </div>
    )
  }

  public render(): JSX.Element {
    const required = formatRequiredIndicator(this.props)
    const className = ['Controlled', 'Input']
    const inputClassName = ['Controlled', 'Input']
    if (this.props.invalid) {
      className.push('invalid')
    }
    if (this.props.className) {
      this.props.className.forEach((cn) => inputClassName.push(cn))
    }
    if (this.state.index === -1) {
      inputClassName.push('selected')
    }
    return (
      <div className="search-select">
        <div className={className.join(' ')} onBlur={this.handleOnBlur.bind(this)}>
          <span className="Label" data-automation={this.props.automation + '-label'} onClick={() => this.input.focus()}>
            {this.props.label}
            {required}
          </span>
          <input
            ref={(input) => {
              this.input = input
            }}
            className={inputClassName.join(' ')}
            data-automation={`${this.props.automation}-input`}
            value={this.state.searchTerm}
            disabled={this.props.disabled}
            onChange={this.handleOnChange.bind(this)}
            onKeyDown={(e) => this.onKeyDown(e)}
            onClick={() => {
              if (this.state.results && this.state.results.length > 0) {
                GlobalEvents.prevent(this.e)
                this.setState({ open: true })
              }
            }}
            placeholder={this.props.placeHolder}
            autoComplete={'there-be-dragons-here'}
          />
          {this.state.searching && <SearchSpinner />}
        </div>
        {this.state.open && this.renderResults()}
      </div>
    )
  }
}
